<template>
    <div class="map-mobile-navigation">
        <template v-if="!isDesktopDevice">
            <div class="nav-top nav-dark-theme" :class="{ 'nav-top-split-view': navigationMode === 'split' }">
                <e-toast as-wrapper :visible="navigationIslandVisible" :max-width="navigationMode !== 'split' ? '190px' : '260px'"
                         :variant="navigationIslandVariant" size="xs" translucent
                         position="bottom-left"
                         :offset-x="navigationMode !== 'split' ? '10px' : '8px'" offset-y="10px"
                         :render-in-body-for-wrapper="false">

                    <template #default>
                        <div class="nav-container" :class="{ 'nav-container-split-view': navigationMode === 'split', 'nav-container-with-location': currentInstruction?.currentLocation && !rerouteState }" @click="openInstructionsList">
                            <template v-if="rerouteState">
                                <div class="nav-icon bg-warning">
                                    <i :class="[fontAwesomePrefix, 'fa-triangle-exclamation']" />
                                </div>
                                <div class="nav-text">
                                    <div class="instruction">
                                        {{ $t('map.assistant.rerouteAlert') }}
                                    </div>
                                </div>

                                <div class="nav-actions">
                                    <b-btn @click.stop="$emit('reroute')" class="ml-2" style="width: 90px;" size="md" variant="primary">
                                        {{ $t('map.assistant.rerouteConfirm') }}</b-btn>
                                </div>
                            </template>
                            <template v-else>
                                <div class="navigation-item-header">
                                    <div class="nav-icon">
                                        <i :class="['fas', currentInstruction?.icon || 'fa-location-arrow']" />
                                    </div>
                                    <div class="nav-instruction-content">
                                        <div v-if="currentInstruction?.currentLocation && !isStartPoint && !isArrived" class="nav-location-banner">
                                            <i :class="['fas', 'fa-location-dot']" />
                                            <span class="text-ellipsis">
                                                {{ currentInstruction?.currentLocation }}
                                            </span>
                                        </div>
                                        <div class="nav-text">
                                            <map-instruction :map-instruction="currentInstruction" no-optional-instructions dynamic-font-size>
                                                <!-- <template #after>
                                                    <i class="fas fa-chevron-down text-muted"/>
                                                </template> -->
                                            </map-instruction>
                                        </div>
                                    </div>
                                    <div class="nav-actions">
                                        <b-btn variant="light" pill class="ratio-1x1" @click.stop="toggleTextToSpeech">
                                            <i :class="[fontAwesomePrefix, muted ? 'fa-volume-xmark' : 'fa-volume-high']" />
                                        </b-btn>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </template>

                    <template v-if="navigationIslandIcon" #icon>
                        <div class="navigation-island-action" :class="{ clickable: navigationIslandClickable }"
                             @click.stop="onNavigationIslandClick">

                            <i :class="navigationIslandIcon" />
                        </div>
                    </template>

                    <template #toast>
                        <div class="optional-instructions navigation-island-action" :class="{ clickable: navigationIslandClickable }"
                             @click.stop="onNavigationIslandClick">

                            {{ navigationIslandLabel }}
                        </div>
                    </template>
                </e-toast>

                <div class="nav-expanded-shell" :class="{ 'nav-expanded-shell-visible': instructionsListVisible }">
                    <div class="nav-expanded-shell-inner">
                        <div class="nav-container-header">
                            <div class="nav-container-title">
                                <div class="nav-container-label">{{ $t('map.assistant.pathTo') }}</div>
                                <div class="nav-container-name line-clamp-1">{{ destinationName }}</div>
                            </div>
                        </div>
                        <div v-for="(instruction, instructionIndex) in navPath.instructions" :key="instructionIndex"
                             class="navigation-item" :class="{ 'current-instruction': index === instructionIndex }"
                             @click.stop="setIndex(instructionIndex); instructionsListVisible = false">
                            <div class="nav-icon">
                                <i :class="['fas', instruction.icon || 'fa-location-arrow']" />
                            </div>
                            <div class="nav-text">
                                <map-instruction :map-instruction="instruction" no-line-clamp/>
                            </div>
                        </div>
                    </div>
                    <div class="arrow-down-container nav-expanded-close" @click.stop="toggleInstructionsList">
                        <div class="arrow-down" />
                    </div>
                </div>

                <div class="progress">
                    <div class="track" :style="{ width: progressPercent }" />
                </div>
                <div class="arrow-down-container nav-progress-tab" @click.stop="toggleInstructionsList">
                    <div class="arrow-down" />
                </div>
            </div>
        </template>

        <div v-if="navigationMode !== 'map'" class="pano-overlay" :class="[navigationMode, { 'desktop-ar-fullscreen': isDesktopARMode }]">
            <div v-if="navigationMode === 'split'" class="pano-actions" :class="{ 'pano-action-fullscreen': navigationMode === 'ar' }">
                <div class="pano-actions-left">
                    <!-- <b-btn variant="translucent-dark" class="pill-btn-xl rounded-xl" @click="displayModalVisible = true">
                        <i class="fa-solid fa-map-location-dot text-accent" style="font-size: 18pt" />
                    </b-btn> -->

                    <!-- <b-btn variant="translucent-dark" class="pill-btn-xl fake-btn-outside mt-3" @click="displayMobileMenu = true">
                        <i class="fa-solid fa-ellipsis"/>
                    </b-btn> -->

                    <e-action-sheet v-if="displayMobileMenu" v-model="displayMobileMenu" :description="$t('map.choseAction')">
                        <e-action-sheet-item v-if="currentInstruction?.image360" @click="displayModalVisible = true">
                            <i :class="[fontAwesomePrefix, navigationMode !== 'map' ? 'fa-map-location-dot' : 'fa-street-view']" class="ml-1 mr-2" />
                            {{ $t('map.assistant.changeView') }}
                        </e-action-sheet-item>
                        <e-action-sheet-item @click="$emit('share-link')">
                            <i :class="[fontAwesomePrefix, 'fa-share-nodes']" class="mr-2" />
                            {{ $t('map.assistant.shareRoute') }}
                        </e-action-sheet-item>
                        <e-action-sheet-item @click="errorReport.visible = true">
                            <i :class="[fontAwesomePrefix, 'fa-triangle-exclamation']" class="mr-2" />
                            {{ $t('map.assistant.reportErrorInStep') }}
                        </e-action-sheet-item>
                        <e-action-sheet-item @click="toggleTextToSpeech">
                            <i :class="[fontAwesomePrefix, muted ? 'fa-volume-xmark' : 'fa-volume-high', 'mr-2']" />
                            {{ muted ? $t('map.assistant.enableTextToSpeech') : $t('map.assistant.disabledTextToSpeech') }}
                        </e-action-sheet-item>
                        <e-action-sheet-item @click="abandonConfirmVisible = true" class="text-danger">
                            <i :class="[fontAwesomePrefix, 'fa-xmark']" class="mr-2" />
                            {{ $t('map.assistant.exitNavigation') }}
                        </e-action-sheet-item>
                        <e-action-sheet-item v-if="cmsWebsite.settings.testing?.enabled" @click="$store.commit(`showFeedbackModal`, {action: `split-view-actions`, instruction: currentInstruction})">
                            <i :class="[fontAwesomePrefix, 'fa-comment-smile', 'mr-2']" />
                            <span v-tr>Give Feedback|Donner des commentaires</span>
                        </e-action-sheet-item>
                    </e-action-sheet>
                </div>

                <div class="pano-actions-right mobile-stacked-actions">
                    <b-btn :variant="arMode ? 'accent' : 'translucent-dark'" @click="toggleARMode()" class="pill-btn-xl">
                        <i :class="[fontAwesomePrefix, 'fa-mobile-rotate-reverse']" />
                        <span v-if="arMode">{{ $t('map.assistant.disabledArMode') }}</span>
                    </b-btn>

                    <b-btn variant="translucent-dark" @click="$emit('reset-camera')" class="pill-btn-xl">
                        <i :class="[fontAwesomePrefix, 'fa-crosshairs']" />
                        <span v-if="showRecenterLabel">{{ $t('map.assistant.resetCameraCount') }}</span>
                    </b-btn>

                    <b-btn variant="translucent-dark" class="pill-btn-xl text-danger" :class="{'fake-btn-outside': navigationMode === 'split'}" @click.stop="abandonConfirmVisible = true">
                        <i :class="[fontAwesomePrefix, 'fa-xmark']" />
                    </b-btn>
                </div>
            </div>

            <div v-if="isDesktopARMode" class="desktop-ar-top">
                <div class="desktop-ar-current">
                    <div class="nav-icon">
                        <i :class="['fas', currentInstruction?.icon || 'fa-location-arrow']" />
                    </div>
                    <div class="nav-text">
                        <map-instruction :map-instruction="currentInstruction"/>
                    </div>
                </div>
                <div class="desktop-ar-top-actions">
                    <b-btn variant="white" pill size="md" class="border ratio-1x1" @click="setDisplayMode('map')">
                        <i :class="[fontAwesomePrefix, 'fa-xmark']" />
                    </b-btn>
                </div>
            </div>

            <div class="pano-overlay-content">
                <template v-if="mapIntegrations.isImages360Enabled()">
                    <pano-360-pannellum v-if="currentInstruction?.image360" ref="arPano" :current-instruction="currentInstruction"
                                        :is-arrived="isArrived"
                                        :is-first-step="index === 0"
                                        :fullscreen="isDesktopARMode"
                                        :mode-ar.sync="arMode"
                                        hide-actions
                                        @next-instruction="onPanoNextInstruction"
                                        @ar-needs-recalibration="onPanoArNeedsRecalibration" />
                    <!-- FOR TESTING PURPOSES ONLY WHEN WE DO NOT HAVE A PHOTO -->
                    <pano-360-pannellum v-else-if="(!isLive() || showArPanoGridInLive) && !currentInstruction?.image && !isArrived" ref="arPanoGrid" :src="require('@/assets/img/maps/test/grid-360.webp')"
                                        :current-instruction="currentInstruction" :is-arrived="isArrived"
                                        :is-first-step="index === 0"
                                        :fullscreen="isDesktopARMode"
                                        :mode-ar.sync="arMode"
                                        :grid-mode-glyph="currentInstruction.currentItem.glyph"
                                        grid-mode
                                        hide-actions
                                        @next-instruction="onPanoNextInstruction"
                                        @ar-needs-recalibration="onPanoArNeedsRecalibration" />
                    <b-img v-else :src="currentInstruction?.image" fluid class="pano-fallback" />
                </template>

                <div v-else class="pano-fallback">
                    <b-img :src="currentInstruction?.image" fluid />
                    <div v-if="currentInstruction?.instructions" class="optional-instruction">
                        <div class="optional-instructions-glyph">
                            <i :class="[fontAwesomePrefix, currentInstruction?.instructionGlyph]" />
                        </div>
                        <div class="optional-instructions">{{ currentInstruction?.instructions }}</div>
                    </div>
                </div>

                <div v-if="isDesktopARMode" class="desktop-ar-footer">
                    <b-btn variant="translucent-dark" class="action-btn ratio-1x1" size="lg" pill :disabled="index === 0"
                           @click="setDesktopIndex(index - 1)">
                        <i :class="[fontAwesomePrefix, 'fa-chevron-left']" />
                    </b-btn>
                    <b-btn variant="accent" class="action-btn" :class="{ 'end-btn': isArrived }" size="lg" pill
                           @click="isArrived ? onEndButtonClick() : setDesktopIndex(index + 1)">
                        <span v-if="isArrived">{{ $t('map.assistant.end') }}</span>
                        <span v-else>{{ $t('map.assistant.next') }}</span>
                        <i :class="[fontAwesomePrefix, 'fa-chevron-right']" class="ml-2" />
                    </b-btn>
                </div>
            </div>
        </div>

        <e-drawer v-if="!isDesktopARMode" ref="navigationAssistantDrawer"
                  class="navigation-assistant-drawer" :snap-points="['130px']" top-mobile-margin="140px"
                  :sticky-header="isDesktopDevice" :sidebar="isDesktopDevice" :title="destinationName" expand-on-header-click
                  :label="$t('map.assistant.pathTo')" header-class="navigation-assistant-sheet-header" :collapse-top-actions="isMobileDevice" :no-share-button="navigationMode === 'split' || isDesktopDevice" :no-content-padding="isDesktopDevice"
                  @above-hidden-change="onDrawerAboveHiddenChange" @close="abandonConfirmVisible = true" @share="$emit('share-link')">

            <template #top-actions-end v-if="isDesktopDevice">
                <b-dropdown size="md" variant="light" class="top-actions-menu" no-caret right>
                    <template #button-content>
                        <i :class="[fontAwesomePrefix, 'fa-ellipsis']" />
                    </template>
                    <b-dropdown-item v-if="currentInstruction?.image360" @click="displayModalVisible = true">
                        <i :class="[fontAwesomePrefix, navigationMode !== 'map' ? 'fa-map-location-dot' : 'fa-street-view']" class="mr-2" />
                        {{ $t('map.assistant.changeView') }}
                    </b-dropdown-item>
                    <b-dropdown-item @click="$emit('share-link')">
                        <i :class="[fontAwesomePrefix, 'fa-share-nodes']" class="mr-2" />
                        {{ $t('map.assistant.shareRoute') }}
                    </b-dropdown-item>
                    <b-dropdown-item @click="errorReport.visible = true">
                        <i :class="[fontAwesomePrefix, 'fa-triangle-exclamation']" class="mr-2" />
                        {{ $t('map.assistant.reportErrorInStep') }}
                    </b-dropdown-item>
                    <b-dropdown-item @click="toggleTextToSpeech">
                        <i :class="[fontAwesomePrefix, muted ? 'fa-volume-xmark' : 'fa-volume-high']" class="mr-2" />
                        {{ muted ? $t('map.assistant.enableTextToSpeech') : $t('map.assistant.disabledTextToSpeech') }}
                    </b-dropdown-item>
                    <b-dropdown-item @click="abandonConfirmVisible = true" class="text-danger">
                        <i :class="[fontAwesomePrefix, 'fa-xmark']" class="mr-2" />
                        {{ $t('map.assistant.exitNavigation') }}
                    </b-dropdown-item>
                </b-dropdown>
            </template>
            <template #above>
                <template v-if="isMobileDevice && mapIntegrations.isImages360Enabled()">
                    <div v-if="navigationMode === 'map' && currentInstruction?.image360" class="preview-mode" @click="displayModalVisible = true">
                        <pano-360-pannellum :current-instruction="currentInstruction"
                                            :fullscreen="false" :is-arrived="isArrived"
                                            :is-first-step="index === 0"
                                            preview />

                        <div class="expand-btn">
                            <i class="fa-solid fa-arrows-up-down fa-rotate-by" />
                        </div>
                    </div>
                    <!-- <b-btn v-else variant="white" @click="displayModalVisible = true" class="pill-btn-xl rounded-xl">
                        <i class="fa-solid fa-map-location-dot fa-xl text-accent" style="font-size: 18pt" />
                    </b-btn> -->
                </template>
                <template v-else-if="isMobileDevice && currentImage">
                    <div class="image-preview" @click="currentImageModal = true">
                        <b-img :src="currentImage" fluid />
                        <div class="next-step-preview-badge">
                            <div class="next-step-preview-name text-ellipsis">{{ $t(`map.assistant.nextStep`) }}</div>
                        </div>
                    </div>
                </template>

                <div class="d-flex mt-auto w-100 align-items-center pointer-none position-relative" style="pointer-events: none">
                    <!-- <div v-if="navigationMode === 'split'" class="pano-actions-right mobile-stacked-actions">
                        <b-btn :variant="arMode ? 'accent' : 'white'" @click="toggleARMode()" class="pill-btn-xl">
                            <i :class="[fontAwesomePrefix, 'fa-mobile-rotate-reverse']" />
                            <span v-if="arMode">{{ $t('map.assistant.disabledArMode') }}</span>
                        </b-btn>

                        <b-btn variant="white" @click="$emit('reset-camera')" class="pill-btn-xl">
                            <i :class="[fontAwesomePrefix, 'fa-crosshairs']" />
                            <span v-if="showRecenterLabel">{{ $t('map.assistant.resetCameraCount') }}</span>
                        </b-btn>

                        <b-btn variant="white" class="pill-btn-xl" @click.stop="abandonConfirmVisible = true">
                            <i :class="[fontAwesomePrefix, 'fa-xmark']" />
                        </b-btn>
                    </div> -->
                    <div v-if="navigationMode !== 'split'" class="mobile-stacked-actions">
                        <b-btn :variant="muted ? 'white' : 'accent'" class="pill-btn-xl" @click.stop="toggleTextToSpeech">
                            <i :class="[fontAwesomePrefix, muted ? 'fa-volume-xmark' : 'fa-volume-high']" />
                        </b-btn>
                        <b-btn v-if="isQrCodeEnabled" variant="white" class="pill-btn-xl" @click="$emit('open-qr-scanner')">
                            <i :class="[fontAwesomePrefix, 'fa-qrcode-read']" />
                        </b-btn>
                        <b-btn v-if="navigationMode !== 'ar'" variant="white" class="pill-btn-xl" @click="$emit('reset-camera')">
                            <i :class="[fontAwesomePrefix, 'fa-crosshairs']" />
                            <span v-if="showRecenterLabel">{{ $t('map.assistant.resetCameraCount') }}</span>
                        </b-btn>
                        <b-btn v-if="currentInstruction?.image360 && navigationMode !== 'map'" :variant="arMode ? 'accent' : 'white'" class="pill-btn-xl" @click="toggleARMode">
                            <i :class="[fontAwesomePrefix, 'fa-mobile-rotate-reverse']" />
                            <span v-if="arMode">{{ $t('map.assistant.disabledArMode') }}</span>
                        </b-btn>
                    </div>
                </div>
            </template>

            <template #header v-if="isDesktopDevice && navigationMode === 'map'">
                <div v-if="currentInstruction?.image360 && !isArrived && mapIntegrations.isImages360Enabled()" class="desktop-ar-preview">
                    <pano-360-pannellum :current-instruction="currentInstruction"
                                        :fullscreen="false" :is-arrived="isArrived"
                                        :is-first-step="index === 0" preview />

                    <div class="expand-btn expand-desktop" @click="setDisplayMode('ar')">
                        <i class="fa-solid fa-arrows-up-down fa-rotate-by" style="--fa-rotate-angle: 45deg;" />
                        <div>{{ $t('map.displayMode.arView') }}</div>
                    </div>
                </div>
                <div v-else-if="!isArrived && currentImage" class="desktop-image">
                    <div class="arrival-modal">
                        <b-img class="arrival-image cursor-pointer" :src="currentImage" fluid @click="currentImageModal = true" />
                        <div class="next-step-preview-badge">
                            <div class="next-step-preview-name text-ellipsis">{{ $t(`map.assistant.nextStep`) }}</div>
                        </div>
                    </div>
                </div>
                <div v-else class="desktop-image">
                    <div class="arrival-modal">
                        <div v-if="isArrived" class="arrival-modal-header">
                            <div class="nav-icon">
                                <i :class="['fas', 'fa-flag-checkered']" />
                            </div>
                            <div class="nav-text">
                                <map-instruction :map-instruction="currentInstruction" no-optional-instructions/>
                            </div>
                        </div>

                        <div class="position-relative">
                            <b-img v-if="previewImage" class="arrival-image cursor-pointer" :src="previewImage" fluid @click="imageModal = true" />
                            <div class="next-step-preview-badge">
                                <div class="next-step-preview-name text-ellipsis">{{ $t(`map.destination`) }}</div>
                            </div>
                        </div>

                        <div v-if="currentInstruction?.instructions" class="optional-instruction">
                            <div v-if="currentInstruction?.instructionGlyph" class="optional-instructions-glyph">
                                <i :class="[fontAwesomePrefix, currentInstruction?.instructionGlyph]" />
                            </div>
                            <div class="optional-instructions">{{ currentInstruction?.instructions }}</div>
                        </div>
                    </div>
                </div>
                <div class="d-flex py-3">
                    <b-btn variant="light" size="lg" class="w-100 mr-2 rounded-lg" :disabled="index === 0"
                           @click="setDesktopIndex(index - 1)">
                        <i :class="[fontAwesomePrefix, 'fa-chevron-left']" class="mr-2" />
                        {{ $t('map.assistant.previous') }}
                    </b-btn>
                    <b-btn variant="accent" size="lg" class="w-100 rounded-lg" @click="isArrived ? onEndButtonClick() : setDesktopIndex(index + 1)">
                        {{ isArrived ? $t('map.assistant.end') : $t('map.assistant.next') }}
                        <i :class="[fontAwesomePrefix, 'fa-chevron-right']" class="ml-2" />
                    </b-btn>
                </div>
            </template>

            <template v-if="isMobileDevice" #header-actions>
                <b-btn variant="light" size="lg" class="w-100 mr-2 rounded-lg" :disabled="index === 0"
                       @click="setDesktopIndex(index - 1)">
                    <i :class="[fontAwesomePrefix, 'fa-chevron-left']" class="mr-2" />
                    {{ $t('map.assistant.previous') }}
                </b-btn>
                <b-btn variant="accent" size="lg" class="w-100 rounded-lg" @click="isArrived ? onEndButtonClick() : setDesktopIndex(index + 1)">
                    {{ isArrived ? $t('map.assistant.end') : $t('map.assistant.next') }}
                    <i :class="[fontAwesomePrefix, 'fa-chevron-right']" class="ml-2" />
                </b-btn>
            </template>

            <template v-if="isDesktopDevice && !isArrived">
                <div ref="desktopInstructionsContainer" class="desktop-navigation-list">
                    <div v-for="(instruction, instructionIndex) in navPath.instructions" :key="instructionIndex"
                         ref="desktopInstructionRows" ref-in-for class="navigation-item"
                         :class="{ 'current-instruction': index === instructionIndex }"
                         @click.stop="setDesktopIndex(instructionIndex)">
                        <div class="nav-icon">
                            <i :class="['fas', instruction.icon || 'fa-location-arrow']" />
                        </div>
                        <div class="nav-text">
                            <map-instruction :map-instruction="instruction" no-line-clamp/>
                            <!--                            <div class="instruction" style="line-clamp: unset; -webkit-line-clamp: unset;">-->
                            <!--                                <span v-if="instruction.showDistance && instruction.nextInstruction?.distance">-->
                            <!--                                    {{ instruction.action }} {{ formatDistance(instruction.nextInstruction.distance) }}-->
                            <!--                                </span>-->
                            <!--                                {{ instruction.description }}-->
                            <!--                                <b v-if="instruction.nextLocation" class="highlight">{{ instruction.nextLocation }}</b>-->
                            <!--                            </div>-->
                        </div>
                    </div>
                </div>
            </template>

            <div v-else-if="isMobileDevice" class="navigation-actions">
                <article role="button" class="navigation-actions-button" @click="openInstructionsList">
                    <i :class="[fontAwesomePrefix, 'fa-list fa-lg']" />
                    {{ $t('map.assistant.instructionsList') }}
                </article>
                <article role="button" class="navigation-actions-button" @click="$emit('open-qr-scanner')">
                    <i :class="[fontAwesomePrefix, 'fa-qrcode-read fa-lg']" />
                    {{ $t('map.assistant.qrCodeScanner.open') }}
                </article>
                <article role="button" class="navigation-actions-button" @click="displayModalVisible = true">
                    <i :class="[fontAwesomePrefix, navigationMode !== 'map' ? 'fa-map-location-dot' : 'fa-street-view' ,'fa-lg']" />
                    {{ $t('map.assistant.changeView') }}
                </article>
                <article role="button" class="navigation-actions-button" @click="$emit('share-link')">
                    <i :class="[fontAwesomePrefix, 'fa-share-nodes fa-lg']" />
                    {{ $t('map.assistant.shareRoute') }}
                </article>
                <article role="button" class="navigation-actions-button" @click="errorReport.visible = true">
                    <i :class="[fontAwesomePrefix, 'fa-triangle-exclamation fa-lg']" />
                    {{ $t('map.assistant.reportErrorInStep')}}
                </article>
                <article role="button" class="navigation-actions-button" @click="toggleTextToSpeech">
                    <i :class="[fontAwesomePrefix, muted ? 'fa-volume-xmark' : 'fa-volume-high', 'fa-lg']" />
                    {{ muted ? $t('map.assistant.enableTextToSpeech') : $t('map.assistant.disabledTextToSpeech') }}
                </article>

                <article v-if="cmsWebsite.settings.testing?.enabled" @click="$store.commit(`showFeedbackModal`, {action: `split-view-actions`, instruction: currentInstruction})" role="button" class="navigation-actions-button">
                    <i :class="[fontAwesomePrefix, 'fa-comment-smile fa-lg']" />
                    <span v-tr>Give Feedback|Donner des commentaires</span>
                </article>

                <article role="button" class="navigation-actions-button text-danger" @click="abandonConfirmVisible = true">
                    <i :class="[fontAwesomePrefix, 'fa-xmark fa-lg', 'text-danger']" />
                    {{ $t('map.assistant.exitNavigation') }}
                </article>
            </div>
        </e-drawer>

        <e-mobile-modal v-model="displayModalVisible" :hide-close="true" position="center">
            <div class="display-modal">
                <div class="title">{{ $t('map.displayMode.title') }}</div>
                <div class="options">
                    <button class="option" :class="{ active: navigationMode === 'map' }" @click="setDisplayMode('map')">
                        <img src="@/assets/img/navigation-assistant-mode/map-preview.webp" class="thumb map-thumb" />
                        <div class="display-label">{{ $t('map.displayMode.map') }}</div>
                    </button>
                    <button class="option" :class="{ active: navigationMode === 'ar' }" @click="setDisplayMode('ar')">
                        <img src="@/assets/img/navigation-assistant-mode/ar-preview.webp" class="thumb map-thumb" />
                        <div class="display-label">{{ $t('map.displayMode.arView') }}</div>
                    </button>
                    <button v-if="!isDesktopDevice" class="option" :class="{ active: navigationMode === 'split' }"
                            @click="setDisplayMode('split')">
                        <img src="@/assets/img/navigation-assistant-mode/split-view-preview.webp"
                             class="thumb map-thumb" />
                        <div class="display-label">{{ $t('map.displayMode.splitView') }}</div>
                    </button>
                </div>

                <!--                <b-btn variant="outline" size="lg" class="option text-danger justify-content-center mt-5" block @click="abandonConfirmVisible = true">-->
                <!--                    {{ $t('map.assistant.exitNavigation') }}-->
                <!--                </b-btn>-->
            </div>
        </e-mobile-modal>

        <e-mobile-modal v-model="floorModal.isVisible"
                        :close-on-backdrop="false" :hide-close="true"
                        :position="isMobileDevice ? 'bottom' : 'center'"
                        description-first :description="$t('map.floorChange')"
                        :title="floorChangeDetails.direction === 'up' ? $t(`map.goUpTo`, { floor: floorChangeDetails.floorName }) : $t(`map.goDownTo`, { floor: floorChangeDetails.floorName })"
        >
            <div class="elevator-modal">
                <template v-if="floorChangeDetails.type === `elevator`">
                    <div class="floor-visual">
                        <div class="floor-indicator" :class="{ 'direction-up': floorChangeDetails.direction === 'up' }">
                            {{ floorChangeDetails.floorLevel }}
                        </div>
                        <b-img class="elevator-image" :src="require(`@/assets/icons/elevator-preview.webp`)" />
                        <div class="floor-direction large" :class="`direction-${floorChangeDetails.direction}`">
                            <i class="fas" :class="floorChangeDetails.direction === 'up' ? 'fa-up-long' : 'fa-down-long'" />
                        </div>
                    </div>
                    <div v-if="floorModal.blocking && isMobileDevice" class="text-center">
                        {{ $t(`map.assistant.stairsTransitionPrompt`, { floor: floorChangeDetails.floorName }) }}
                    </div>
                </template>

                <template v-else-if="floorChangeDetails.type === `stair`">
                    <div class="floor-visual">
                        <b-img class="stair-image" :src="require(`@/assets/icons/stair-${floorChangeDetails.direction}.svg`)" />
                    </div>
                    <div v-if="floorModal.blocking && isMobileDevice" class="text-center">
                        {{ $t(`map.assistant.stairsTransitionPrompt`, { floor: floorChangeDetails.floorName }) }}
                    </div>
                </template>

                <div class="actions mt-3">
                    <b-btn variant="accent" size="lg" class="action-btn w-100" @click="continueAfterFloorChange">
                        {{ floorModalPrimaryLabel }}
                    </b-btn>
                    <b-btn v-if="floorModal.blocking && isMobileDevice" variant="light" size="lg" class="action-btn w-100 mt-2" @click="cancelFloorChangeModal">
                        {{ $t('map.assistant.elevator.cancel') }}
                    </b-btn>
                </div>
            </div>
        </e-mobile-modal>

        <e-mobile-modal class="arrival-modal-container" v-if="canDisplayArrivalModal" v-model="arrivalModalVisible" :close-on-backdrop="false" :hide-close="true" position="center">
            <div class="arrival-modal">
                <div class="arrival-modal-header">
                    <div class="nav-icon">
                        <i :class="[fontAwesomePrefix, 'fa-flag-checkered']" />
                    </div>
                    <div class="nav-text">
                        <map-instruction :map-instruction="currentInstruction" no-optional-instructions/>
                        <!--                        <div class="instruction">-->
                        <!--                            {{ currentInstruction?.description }}-->
                        <!--                            <b v-if="currentInstruction?.nextLocation" class="highlight">-->
                        <!--                                {{ currentInstruction.nextLocation }}-->
                        <!--                            </b>-->
                        <!--                        </div>-->
                    </div>
                </div>

                <b-img v-if="previewImage" class="arrival-image" :src="previewImage" fluid />

                <div v-if="currentInstruction?.instructions" class="optional-instruction">
                    <div class="optional-instructions-glyph">
                        <i :class="[fontAwesomePrefix, currentInstruction?.instructionGlyph]" />
                    </div>
                    <div class="optional-instructions">{{ currentInstruction?.instructions }}</div>
                </div>
            </div>
            <template #footer>
                <b-btn variant="light" size="lg" class="action-btn w-50" @click="arrivalModalVisible = false">
                    {{ $t('map.assistant.previous') }}
                </b-btn>
                <b-btn variant="accent" size="lg" class="action-btn w-50" @click="endAndRequestFeedback">
                    {{ $t('map.assistant.end') }}
                </b-btn>
            </template>
        </e-mobile-modal>

        <e-mobile-modal class="feedback-modal-container" v-model="feedbackModalVisible" :close-on-backdrop="false" position="center">
            <template #above-body>
                <div class="icon">
                    <i :class="[fontAwesomePrefix, 'fa-face-smile']" />
                </div>
            </template>
            <div class="feedback-modal">
                <div class="title">{{ $t('map.assistant.experienceQuestion') }}</div>
                <b-form-rating v-model="rating" size="xl" variant="warning" inline no-border class="mb-2 rounded" />
                <div class="comment-label">{{ $t('map.assistant.commentOptional') }}</div>
                <b-textarea v-model="feedbackMessage" no-resize :placeholder="$t('map.assistant.commentPlaceholder')" class="rounded-lg" style="min-height: 80px"/>

                <b-media v-if="cmsWebsite.dealerid === 'muhc-glen-campus'" class="mt-4 rounded p-2" @click="openMuhcCommiteeWebsite"
                         style="border: 1px solid #7465ad; box-shadow: 0 2px 6px #7465ad47; background: linear-gradient(135deg, #ffffff, #7465ad14);">
                    <template #aside>
                        <b-img width="100" src="https://storage.eyein.media/cms/muhc-glen-campus/muhc-user-commitee-logo.webp"/>
                    </template>
                    <div class="text-left" v-tr>
                        This service was made possible thanks to the MUHC Users' Patients' Committee!|
                        Ce service a été rendu possible grâce au comité des usagers du CUSM!
                    </div>
                </b-media>
            </div>
            <template #footer="{ close }">
                <b-btn variant="light" size="lg" class="action-btn w-100" @click="close($emit(`reset-navigation`))">
                    {{ $t('map.assistant.skipFeedback') }}
                </b-btn>
                <b-btn variant="accent" size="lg" class="action-btn send-feedback w-100" :disabled="!rating"
                       @click="close(sendFeedback)">
                    {{ $t('map.assistant.sendFeedback') }}
                </b-btn>
            </template>
        </e-mobile-modal>
        <e-mobile-modal v-model="errorReport.visible" :close-on-backdrop="false" :hide-close="true" position="center"
                        :title="$t('map.assistant.feedbackModalTitle')">
            <b-form>
                <div class="text-center">
                    <i class="fa-triangle-exclamation text-warning fa-2x mb-2" :class="fontAwesomePrefix"
                       style="opacity: 0.75" />
                </div>
                <b-form-group>
                    <template #label>
                        <span class="f-11">{{$t('map.assistant.subject')}}</span>
                    </template>
                    <b-select v-model="errorReport.issue" required>
                        <option value="unclear-instructions">{{ $t('map.assistant.issueOptions.unclearInstructions') }}
                        </option>
                        <option value="image-obsolete">{{ $t('map.assistant.issueOptions.imageObsolete') }}</option>
                        <option value="image-wrong">{{ $t('map.assistant.issueOptions.imageWrong') }}</option>
                        <option value="image-missing">{{ $t('map.assistant.issueOptions.imageMissing') }}</option>
                        <option value="missing-instructions">{{ $t('map.assistant.issueOptions.missingInstructions') }}
                        </option>
                        <option value="under-construction">{{ $t('map.assistant.issueOptions.underConstruction') }}
                        </option>
                        <option value="location-moved">{{ $t('map.assistant.issueOptions.locationMoved') }}</option>
                        <option value="signage-changed">{{ $t('map.assistant.issueOptions.signageChanged') }}</option>
                        <option value="cannot-go-to-next">{{ $t('map.assistant.issueOptions.cannotGoNext') }}</option>
                        <option value="cannot-end-navigation">{{ $t('map.assistant.issueOptions.cannotEndNavigation') }}
                        </option>
                        <option value="improvement">{{ $t('map.assistant.issueOptions.improvement') }}</option>
                        <option value="other">{{ $t('map.assistant.issueOptions.other') }}</option>
                        <option value="test">{{ $t('map.assistant.issueOptions.test') }}</option>
                    </b-select>
                </b-form-group>
                <b-form-group>
                    <template #label>
                        <span class="f-11">{{$t('map.assistant.comment')}}</span>
                    </template>
                    <b-textarea v-model="errorReport.message" :placeholder="$t('map.assistant.commentDescription')"
                                no-resize />
                </b-form-group>
                <b-checkbox v-model="errorReport.contactMe">
                    <span class="f-11">{{ $t('map.assistant.contactPermission') }}</span>
                </b-checkbox>
                <template v-if="errorReport.contactMe">
                    <b-form-group :label="$t('map.assistant.email')">
                        <b-input-group>
                            <b-input-group-text><i class="fa-envelope" :class="fontAwesomePrefix" />
                            </b-input-group-text>
                            <b-form-input type="email" v-model="errorReport.email" placeholder="Ex: john.doe@email.com " />
                        </b-input-group>
                    </b-form-group>
                </template>
            </b-form>
            <template #footer="{ close }">
                <b-btn variant="light" class="action-btn w-100 py-2" @click="close">
                    {{ $t('map.assistant.cancel') }}
                </b-btn>
                <b-btn variant="accent" class="action-btn w-100 py-2" :disabled="!errorReport.issue"
                       @click="close(sendErrorReport)">
                    {{ $t('map.assistant.sendReport') }}
                </b-btn>
            </template>
        </e-mobile-modal>

        <e-mobile-modal v-model="abandonConfirmVisible" :close-on-backdrop="false" :hide-close="true" position="center">
            <div class="abandon-modal">
                <div class="icon">
                    <i :class="[fontAwesomePrefix, 'fa-triangle-exclamation']" />
                </div>
                <div class="title">{{ $t('map.assistant.abandonTitle') }}</div>
            </div>
            <template #footer="{ close }">
                <b-btn variant="light" size="lg" class="action-btn w-100" @click="close">
                    {{ $t('map.common.cancel') }}
                </b-btn>
                <b-btn variant="danger" size="lg" class="action-btn w-100" @click="close(confirmAndAbandon)">
                    {{ $t('map.common.exit') }}
                </b-btn>
            </template>
        </e-mobile-modal>

        <e-mobile-modal v-model="imageModal" position="center" :title="destinationName">
            <div class="w-100 ratio-1x1 rounded-lg bg-light overflow-hidden">
                <b-img :src="previewImage" :alt="destinationName" block fluid />
            </div>
        </e-mobile-modal>

        <e-mobile-modal v-model="currentImageModal" position="center" :title="$t(`map.assistant.nextStep`)">
            <div class="w-100 ratio-1x1 rounded-lg bg-light overflow-hidden">
                <b-img :src="currentImage" :alt="$t(`map.assistant.nextStep`)" block fluid />
            </div>
        </e-mobile-modal>
    </div>
</template>

<script>
import Pano360Pannellum from "@/components/pano-360-pannellum.vue";
import { formatDistance as helperFormatDistance } from '@/helpers/DistanceHelper.js';
import NetworkV2 from "@vue-components/helpers/NetworkV2.js";
import Feedback from "../../../../common/db/models/core/Feedback.js";
import SessionTracker from "@/helpers/SessionTracker.js";
import {getLocale, isFrench} from '@/../vue-components/helpers/I18nPluginHelper.js';
import { BFormRating } from "bootstrap-vue";
import { swal } from "vue-swal";
import EDrawer from '@/components/e-drawer.vue';
import EMobileModal from '@/components/e-mobile-modal.vue';
import EToast from '@/components/e-toast.vue';
import MapMixin from "@/helpers/maps/map-mixin.js";
import MapInstruction from "@components/maps/mobile/map-instruction.vue";
import EActionSheet from "@vue-components/components/e-action-sheet.vue";
import EActionSheetItem from "@vue-components/components/e-action-sheet-item.vue";

export default {
    name: `map-mobile-navigation-assistant`,
    components: {EActionSheetItem, EActionSheet, MapInstruction, Pano360Pannellum, BFormRating, EDrawer, EMobileModal, EToast },
    inject: { mapCompass: { default: null } },
    mixins: [MapMixin],
    props: {
        navPath: { type: Object, required: true },
        analyticsRoute: { type: Object, required: true },
        compassHeading: { type: Number, default: null },
        rerouteState: { type: Boolean, default: false },
        gpsLocationActive: { type: Boolean, default: false },
        sensorStatusToast: { type: Object, default: null },
        floorNumber: {type: Number, default: null },
        showRecenterLabel: { type: Boolean, default: false }
    },
    data() {
        return {
            index: 0,
            muted: true,
            navigationMode: `map`,
            displayMobileMenu: false,
            displayModalVisible: false,
            arrivalModalVisible: false,
            feedbackModalVisible: false,
            abandonConfirmVisible: false,
            instructionsListVisible: false,
            arrivalSeen: false,
            errorReport: {
                visible: false,
                issue: ``,
                message: ``,
                contactMe: false,
                email: ``
            },
            imageModal: false,
            currentImageModal: false,
            feedbackMessage: ``,
            rating: null,
            speechSynthesis: null,
            speechVoices: [],
            deviceVoice: null,
            arMode: false,
            isDrawerOpened: false,
            floorModal: {
                isVisible: false,
                instruction: null,
                pendingIndex: null,
                blocking: true,
                continueCountdown: 0,
                autoCloseTimer: null,
                countdownTimer: null
            },
        };
    },
    computed: {
        currentInstruction() {
            return this.navPath?.instructions?.[this.index] || null;
        },
        nextInstruction() {
            return this.navPath?.instructions?.[this.index + 1] || null;
        },
        previousInstruction() {
            return this.navPath?.instructions?.[this.index - 1] || null;
        },
        destinationName() {
            return this.navPath.end.getFullName();
        },
        previewInstruction() {
            if (this.navPath && this.navPath.instructions) {
                for (let i = this.index + 1; i < this.navPath.instructions.length; i++) {
                    const instruction = this.navPath.instructions[i];
                    if (instruction.image) {
                        return instruction;
                    }
                }
            }

            return this.nextInstruction || this.currentInstruction || null;
        },
        currentImage() {
            // destination image
            if (this.index === this.navPath.instructions.length - 1) {
                return this.previewImage;
            }
            if (this.navPath && this.navPath.instructions) {
                const nextTwoIndex = this.index + 3;
                const startIndex = this.mapIntegrations.isImages360Enabled() ? this.index : this.index + 1;
                for (let i = startIndex; i < nextTwoIndex; i++) {
                    const instruction = this.navPath.instructions[i];
                    if (instruction && instruction.image) {
                        return instruction.image;
                    }
                }
            }
            return null;
        },
        previewImage() {
            return this.navPath?.previewImage || this.currentInstruction?.image || null;
        },
        isStartPoint() {
            return this.navPath && this.index === 0;
        },
        isArrived() {
            return this.navPath && this.index === this.navPath.instructions.length - 1;
        },
        isDesktopARMode() {
            return this.isDesktopDevice && this.navigationMode === `ar`;
        },
        canDisplayArrivalModal() {
            return this.isMobileDevice && this.navigationMode !== `ar`;
        },
        progressPercent() {
            if (!this.navPath?.instructions?.length || this.navPath.instructions.length <= 1) {
                return `0%`;
            }
            const ratio = this.index / (this.navPath.instructions.length - 1);
            return `${Math.min(100, Math.max(0, ratio * 100))}%`;
        },
        defaultNavigationMode() {
            if (this.mapIntegrations.isImages360Enabled()) {
                return `split`;
            }
            return `map`;
        },
        floorModalInstruction() {
            return this.floorModal.instruction || this.nextInstruction || null;
        },
        floorModalPrimaryLabel() {
            if (this.floorModal.blocking && this.isMobileDevice) {
                return this.$t(`map.assistant.elevator.confirm`);
            }
            const label = this.$t(`map.assistant.continueNavigation`);
            if (!this.floorModal.continueCountdown) {
                return label;
            }
            return `${label} (${String(this.floorModal.continueCountdown).padStart(2, `0`)})`;
        },
        floorChangeDetails() {
            const instruction = this.floorModalInstruction;
            const fromLevel = Number(instruction?.fromFloor?.level);
            const toLevel = Number(instruction?.toFloor?.level);
            const direction = Number.isFinite(fromLevel) && Number.isFinite(toLevel) && toLevel < fromLevel ? `down` : `up`;
            const floorName = instruction?.toFloor?.getName ? instruction.toFloor.getName() : null;

            return {
                direction,
                floorName: this.$tr(floorName),
                floorLevel: instruction?.toFloor?.level ?? this.floorNumber,
                type: instruction?.currentItem.type
            };
        },
        navigationIsland() {
            if (!this.isMobileDevice || this.instructionsListVisible) {
                return null;
            }

            if (this.currentInstruction && this.currentInstruction.instructions) {
                return {
                    variant: `warning`,
                    label: this.currentInstruction.instructions,
                    icon: this.currentInstruction.instructionGlyph ? [this.fontAwesomePrefix, this.currentInstruction.instructionGlyph] : null
                };
            }

            return this.sensorStatusToast;
        },
        navigationIslandVisible() {
            return !!this.navigationIsland && !this.isDrawerOpened;
        },
        navigationIslandVariant() {
            return this.navigationIsland ? this.navigationIsland.variant : `warning`;
        },
        navigationIslandIcon() {
            return this.navigationIsland ? this.navigationIsland.icon : null;
        },
        navigationIslandLabel() {
            return this.navigationIsland ? this.navigationIsland.label : ``;
        },
        navigationIslandClickable() {
            return !!this.navigationIsland && !!this.navigationIsland.clickable;
        }
    },
    mounted() {
        this.setIndex(0, true);
        this.navigationMode = this.defaultNavigationMode;
        if (this.navigationMode !== `map` && this.navSettings.compass && this.isCurrentLocationOnSite && !this.isLocal()) {
            this.toggleARMode();
        }
        this.setDisplayMode(this.navigationMode);
        this.scrollDesktopInstructionToTop(0);
        this.muted = true;
        this.speechSynthesis = window.speechSynthesis;
        const voices = this.speechSynthesis?.getVoices() || [];
        if (voices.length) {
            this.speechVoices = [...voices];
            this.deviceVoice = this.getVoice();
        } else {
            speechSynthesis.onvoiceschanged = () => {
                this.speechVoices = [...this.speechSynthesis.getVoices()];
                this.deviceVoice = this.getVoice();
            };
        }
    },
    beforeDestroy() {
        this.clearFloorChangeModalTimer();
    },
    beforeUnmount() {
        this.clearFloorChangeModalTimer();
    },
    methods: {
        formatDistance(value) {
            return helperFormatDistance(value, { style: `spaced` });
        },
        getActivePanoRef() {
            return this.$refs.arPano || this.$refs.arPanoGrid || null;
        },
        onNavigationIslandClick() {
            if (!this.navigationIslandClickable) {
                return;
            }

            this.$emit(`sensor-status-toast-click`, this.navigationIsland);
        },
        setIndex(index, silent = false, force = false, source = `manual`) {
            const nextIndex = Math.max(Math.min(index, this.navPath.instructions.length - 1), 0);

            if (!force && source === `auto` && nextIndex === this.index) {
                return false;
            }

            if (!force && nextIndex > this.index) {
                let floorChangeInstruction = null;
                let floorChangeInstructionIndex = null;

                for (let i = this.index + 1; i <= nextIndex; i++) {
                    const instruction = this.navPath.instructions[i];
                    const type = instruction?.currentItem?.type;
                    const hasFloorChange = instruction?.fromFloor?.id
                        && instruction?.toFloor?.id
                        && instruction.fromFloor.id !== instruction.toFloor.id;
                    if ((type === `elevator` || type === `stairs`) && hasFloorChange) {
                        floorChangeInstruction = instruction;
                        floorChangeInstructionIndex = i;
                        console.log(`Detected floor change instruction at index ${i}:`, instruction);
                        break;
                    }
                }

                if (floorChangeInstruction) {
                    this.showFloorChangeModal(floorChangeInstruction, {
                        pendingIndex: this.gpsLocationActive ? null : floorChangeInstructionIndex,
                        blocking: !this.gpsLocationActive
                    });

                    if (!this.gpsLocationActive) {
                        return false;
                    }
                }
            }

            this.index = nextIndex;
            this.$emit(`set-instruction`, this.currentInstruction, source);
            this.scrollCurrentInstructionToTop(this.index);
            if (!silent) {
                this.playTextToSpeech();
            }
            if (this.isArrived) {
                this.arrivalSeen = true;
            }
            return true;
        },
        setDesktopIndex(index, silent = false) {
            const changed = this.setIndex(index, silent);
            if (!changed) {
                return false;
            }
            this.scrollDesktopInstructionToTop(this.index);
            return true;
        },
        showFloorChangeModal(instruction, { pendingIndex = null, blocking = true, autoCloseMs = 15000 } = {}) {
            if (!instruction) {
                return;
            }

            this.clearFloorChangeModalTimer();
            this.floorModal.isVisible = true;
            this.floorModal.instruction = instruction;
            this.floorModal.pendingIndex = pendingIndex;
            this.floorModal.blocking = blocking;
            this.floorModal.continueCountdown = 0;

            if (!blocking) {
                this.startFloorChangeCountdown(autoCloseMs);
                this.floorModal.autoCloseTimer = setTimeout(() => {
                    this.hideFloorChangeModal();
                }, autoCloseMs);
            }
        },
        continueAfterFloorChange() {
            const pendingIndex = this.floorModal.pendingIndex;
            this.hideFloorChangeModal();

            if (pendingIndex != null) {
                this.setIndex(pendingIndex + 1, false, true);
            }
        },
        cancelFloorChangeModal() {
            this.hideFloorChangeModal();
        },
        hideFloorChangeModal() {
            this.clearFloorChangeModalTimer();
            this.floorModal.isVisible = false;
            setTimeout(() => {
                this.floorModal.instruction = null;
                this.floorModal.pendingIndex = null;
                this.floorModal.blocking = true;
            }, 1000);
        },
        clearFloorChangeModalTimer() {
            if (this.floorModal.autoCloseTimer) {
                clearTimeout(this.floorModal.autoCloseTimer);
                this.floorModal.autoCloseTimer = null;
            }
            if (this.floorModal.countdownTimer) {
                clearInterval(this.floorModal.countdownTimer);
                this.floorModal.countdownTimer = null;
            }
            this.floorModal.continueCountdown = 0;
        },
        startFloorChangeCountdown(autoCloseMs) {
            const totalSeconds = Math.max(1, Math.ceil((Number(autoCloseMs) || 0) / 1000));
            this.floorModal.continueCountdown = totalSeconds;

            this.floorModal.countdownTimer = setInterval(() => {
                if (this.floorModal.continueCountdown <= 1) {
                    this.floorModal.continueCountdown = 0;
                    if (this.floorModal.countdownTimer) {
                        clearInterval(this.floorModal.countdownTimer);
                        this.floorModal.countdownTimer = null;
                    }
                    return;
                }
                this.floorModal.continueCountdown -= 1;
            }, 1000);
        },
        onDrawerAboveHiddenChange(value) {
            this.isDrawerOpened = value;
            this.$emit(`drawer-opened-change`, value);
        },
        scrollCurrentInstructionToTop(instructionIndex = this.index) {
            this.scrollDesktopInstructionToTop(instructionIndex);
        },
        scrollDesktopInstructionToTop(instructionIndex = this.index) {
            if (!this.isDesktopDevice) {
                return;
            }

            this.$nextTick(() => {
                const drawerEl = this.$refs.navigationAssistantDrawer?.$el;
                const instructionRows = this.$refs.desktopInstructionRows;
                if (!drawerEl || !instructionRows) {
                    return;
                }

                const rows = Array.isArray(instructionRows) ? instructionRows : [instructionRows];

                if (instructionIndex >= rows.length) {
                    return;
                }

                let scrollTop = 0;
                for (let i = 0; i < instructionIndex; i++) {
                    scrollTop += rows[i].offsetHeight;
                }

                drawerEl.scrollTo({
                    top: scrollTop,
                    behavior: `smooth`
                });
            });
        },
        toggleTextToSpeech() {
            this.muted = !this.muted;
            this.muted && this.speechSynthesis?.cancel();
            this.playTextToSpeech();
        },
        onPanoNextInstruction() {
            if (this.isArrived) {
                this.onEndButtonClick();
                return;
            }
            if (this.isDesktopDevice) {
                this.setDesktopIndex(this.index + 1);
                return;
            }
            this.setIndex(this.index + 1);
        },
        playTextToSpeech() {
            if (this.muted || !this.currentInstruction) {
                return;
            }
            const locale = getLocale();
            const langTagMap = { fr: `fr-CA`, ca: `ca-ES`, it: `it-IT`, de: `de-DE` };
            const langTag = langTagMap[locale] || `en-CA`;
            const speech = new SpeechSynthesisUtterance();
            speech.voice = this.deviceVoice;
            speech.voiceURI = `native`;
            speech.lang = langTag;
            speech.text = this.currentInstruction.getTextToSpeech();
            if (this.index === 0) {
                speech.text = `${this.$t(`map.assistant.textToSpeechIntro`)} ${this.destinationName}. ${speech.text}`;
            }
            this.speechSynthesis.cancel();
            this.speechSynthesis.speak(speech);
        },
        getVoice() {
            const locale = getLocale();
            const langTagMap = { fr: `fr-CA`, ca: `ca-ES`, it: `it-IT`, de: `de-DE` };
            const langTag = langTagMap[locale] || `en-CA`;
            const voiceLocale = langTag.split(`-`)[0];
            const preferredVoices = {
                'en-CA': [`Samantha`, `Microsoft Clara`, `Google US English`],
                'fr-CA': [`Amelie`, `Microsoft Antoine Online (Natural) - French (Canada)`, `Google franA\u00e7ais`],
            };
            const preferredVoiceNames = preferredVoices[langTag] || [];
            let selectedVoice = null;
            for (const voiceName of preferredVoiceNames) {
                selectedVoice = this.speechVoices.find(voice => voice.name.startsWith(voiceName) && voice.lang.startsWith(voiceLocale));
                if (selectedVoice) break;
            }
            return selectedVoice || null;
        },
        setDisplayMode(mode) {
            const nextMode = this.isDesktopDevice && mode === `split` ? `map` : mode;
            this.navigationMode = nextMode;
            this.displayModalVisible = false;
            this.resetArMode();
            this.syncArrivalModalVisibility();

            if (nextMode === `ar`) {
                this.$emit(`ar-mode`);
            } else if (nextMode === `map`) {
                this.$emit(`map-mode`);
            } else if (nextMode === `split`) {
                this.$emit(`split-mode`);
            }

            this.$nextTick(() => {
                const pano = this.getActivePanoRef();
                if (pano) {
                    pano.updateContainerSize();
                }
                this.scrollDesktopInstructionToTop(this.index);
            });
        },
        showArrivalModal() {
            if (!this.canDisplayArrivalModal) {
                this.arrivalModalVisible = false;
                return false;
            }
            this.arrivalModalVisible = true;
            return true;
        },
        onEndButtonClick() {
            if (this.isMobileDevice) {
                // only show arrival modal if there are extra instructions or image
                if (this.currentInstruction.image || this.tr(this.currentInstruction.instructions)) {
                    if (!this.showArrivalModal()) {
                        this.endAndRequestFeedback();
                    }
                } else {
                    this.endAndRequestFeedback();
                }
            } else {
                this.arrivalModalVisible = false;
                this.feedbackModalVisible = false;

                requestAnimationFrame(() => {
                    this.$emit(`reset-navigation`);
                });
            }
        },
        endAndRequestFeedback() {
            this.arrivalModalVisible = false;
            this.feedbackModalVisible = true;

            this.$emit(`end-navigation`);
        },
        async sendFeedback() {
            const feedback = new Feedback({
                project: `cms-editor`,
                type: `rating`,
                dealerid: this.dealerid,
                rating: this.rating,
                message: this.feedbackMessage,
                data: this.navPath.instructions,
                websiteId: this.cmsWebsite.id,
                sessionId: SessionTracker.session?.id
            });
            this.feedbackModalVisible = false;

            requestAnimationFrame(() => {
                this.$emit(`reset-navigation`);
            });

            await NetworkV2.post(`/api/public/feedback/send`, feedback)
                .catch(e => console.error(e));
        },

        async sendErrorReport() {
            this.showLoading();
            const errorFeedback = new Feedback({
                project: `cms-editor`,
                type: `error`,
                dealerid: this.dealerid,
                message: this.errorReport.message,
                issue: this.errorReport.issue,
                contactMe: this.errorReport.contactMe,
                email: this.errorReport.email,
                data: this.currentInstruction,
                websiteId: this.cmsWebsite.id,
                sessionId: SessionTracker.session?.id
            });

            await NetworkV2.post(`/api/public/feedback/send`, errorFeedback);
            this.errorReport.message = ``;
            this.errorReport.visible = false;
            swal({
                title: this.$t(`map.assistant.thankYou`),
                text: this.$t(`map.assistant.valuableFeedback`),
                icon: `success`
            });
        },
        confirmAndAbandon() {
            this.$emit(`back-to-trajectory`);
        },
        openInstructionsList() {
            this.instructionsListVisible = true;
        },
        toggleInstructionsList() {
            this.instructionsListVisible = !this.instructionsListVisible;
        },
        syncArrivalModalVisibility() {
            if (!this.canDisplayArrivalModal) {
                this.arrivalModalVisible = false;
            }
        },
        resetArMode() {
            this.arMode = false;
        },
        async toggleARMode() {
            const willActivateArMode = !this.arMode;

            if (willActivateArMode && this.mapCompass) {
                await this.mapCompass.start();
            }

            this.arMode = willActivateArMode;
        },
        async onPanoArNeedsRecalibration() {
            if (!this.arMode) {
                return;
            }
            if (this.mapCompass) {
                await this.mapCompass.start();
            }
            const heading = this.mapCompass.heading ?? null;
            if (heading == null) {
                return;
            }
            const pano = this.getActivePanoRef();
            if (!pano || !pano.modeAR) {
                return;
            }
            pano.setCameraAngle(heading, {force: true});
        },
        openMuhcCommiteeWebsite() {
            if (isFrench()) {
                window.open(`https://cusm.ca/comite-usagers`, `_blank`);
            } else {
                window.open(`https://muhc.ca/patients-committee`, `_blank`);
            }
        }
    },
    watch: {
        navPath() {
            this.index = 0;
            this.arrivalSeen = false;
            this.hideFloorChangeModal();
            this.setIndex(0, true);
            this.scrollDesktopInstructionToTop(0);
        },
        instructionsListVisible(isVisible) {
            if (isVisible && this.navigationMode === `ar`) {
                this.scrollCurrentInstructionToTop(this.index);
            }
        },
        navigationMode() {
            this.syncArrivalModalVisibility();
        }
    },
};
</script>

<style lang="scss" scoped>
@import "./map-mobile-navigation-shared.scss";

.map-mobile-navigation {
    position: fixed;
    inset: 0;
    z-index: 1020;
    pointer-events: none;
}

.nav-top {
    --nav-collapsed-height: 111px;
    pointer-events: auto;
    position: fixed;
    z-index: 1003;
    top: 0;
    left: 0;
    right: 0;
}

.navigation-island-action {
    pointer-events: none;

    &.clickable {
        pointer-events: auto;
        cursor: pointer;
    }
}

.nav-container {
    --map-mobile-nav-instruction-lines: 3;
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: var(--nav-collapsed-height);
    // strongly reduced vertical padding to allow max 3 lines
    padding: 5px 12px 5px 12px;
    overflow: hidden;
    border-radius: 0;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);

    &.nav-container-with-location {
        flex-direction: column;
        align-items: stretch;
        justify-content: center;
        gap: 6px;
        padding-top: 4px;
    }

    //&.nav-container-split-view {
    //    padding: 8px 8px 16px 8px;
    //    align-items: flex-start;
    //    background: transparent;
    //    border-radius: 0;
    //    box-shadow: none;
    //}

    .nav-reroute-container {
        gap: 12px;
        width: 100%;
        display: flex;
        align-items: center;
    }

    .navigation-item-header {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        container-type: inline-size;

        > .nav-icon {
            width: clamp(36px, 12cqi, 46px);
            height: clamp(36px, 12cqi, 46px);
            font-size: clamp(30px, 10cqi, 40px);
        }
    }

    .nav-instruction-content {
        display: flex;
        flex-direction: column;
        min-width: 0;
        flex: 1;
        gap: 6px;
    }

    .nav-text {
        container-type: inline-size;
    }

    .nav-location-banner {
        display: flex;
        align-items: center;
        align-self: flex-start;
        gap: 1px;
        max-width: 100%;
        min-width: 0;
        padding: 4px 8px 4px 4px;
        border-radius: 4px;
        background: #333;
        color: #fff;
        border: 1px solid #565656;
        font-size: 10pt;
        line-height: 1;
        overflow: hidden;

        i {
            flex: 0 0 auto;
            color: var(--color-muted);
        }
    }
}

.nav-expanded-shell {
    --nav-expanded-start-height: 36px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: var(--nav-expanded-start-height);
    opacity: 0;
    background: #0a0a0a;
    --border-color: rgba(255, 255, 255, 0.2);
    color: white;
    transition: height 280ms ease, opacity 180ms ease-out;
    pointer-events: none;
    z-index: 1004;
    overflow: hidden;
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.18);
}

.nav-expanded-shell-visible {
    height: 100dvh;
    opacity: 1;
    pointer-events: auto;
    z-index: 1056;
}

.nav-expanded-shell-inner {
    height: 100%;
    overflow-y: auto;
    padding-bottom: 72px;
}

.nav-expanded-shell .navigation-item {
    padding: 20px 12px;
    background-color: inherit;
}

.nav-expanded-shell .navigation-item + .navigation-item {
    border-top: var(--border-width) solid var(--border-color);
}

.nav-expanded-shell .current-instruction {
    background: #134c5f;
}

.arrow-down-container {
    position: absolute;
    bottom: 7px;
    left: 50%;
    transform: translateX(-50%);

    .arrow-down {
        position: relative;
        width: 36px;
        height: 12px;
    }

    .arrow-down::before,
    .arrow-down::after {
        content: '';
        position: absolute;
        top: 0;
        width: 20px;
        height: 4px;
        background: #ddd;
        border-radius: 999px;
    }

    .arrow-down::before {
        left: 0;
        transform: rotate(20deg);
        transform-origin: left center;
    }

    .arrow-down::after {
        right: 0;
        transform: rotate(-20deg);
        transform-origin: right center;
    }
}

.nav-progress-tab {
    top: calc(var(--nav-collapsed-height) + 5px);
    bottom: auto;
    z-index: 1;
    display: grid;
    place-items: center;
    width: 62px;
    height: 22px;
    border-radius: 0 0 14px 14px;
    background: rgba(0, 0, 0, 0.72);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.18);

    .arrow-down {
        width: 30px;
        height: 10px;
    }

    .arrow-down::before,
    .arrow-down::after {
        width: 17px;
        height: 3px;
        background: #ddd;
    }
}

.nav-expanded-close {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    transform: rotateX(180deg);
    display: grid;
    place-items: center;
    height: 60px;
    padding-top: 12px;
    background: #111;
    box-shadow: 0 -6px 16px rgba(0, 0, 0, 0.08);

    .arrow-down::before,
    .arrow-down::after {
        background: var(--light);
    }
}

.nav-actions {
    margin-left: auto;
}

.progress {
    height: 5px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 0;
    transition: opacity 140ms ease;

    .track {
        height: 5px;
        background: #31ccfc;
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
        transition: width 200ms ease;
    }
}

.floatting-element {
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.7);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
    padding: 8px 12px 0;
}

.pano-overlay {
    pointer-events: auto;
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 100px;

    &.split {
        top: 50dvh;
        bottom: 120px;

        .pano-overlay-content {
            border-top-left-radius: 16px;
            border-top-right-radius: 16px;
        }

        .pano-overlay-handle {
            position: absolute;
            z-index: 1021;
            top: 12px;
            left: 50%;
            transform: translateX(-50%);
            width: 40px;
            height: 5px;
            border-radius: 999px;
            background: rgba(255, 255, 255, 0.6);
        }
    }

    .pano-360 {
        height: 100%;
        width: 100%;
    }

    .pano-overlay-content {
        width: 100%;
        height: 100%;
        overflow: hidden;
        background: var(--color-light);

        &:has(.pano-fallback) {
            display: flex;
            align-items: center;
        }
    }

    &:not(.desktop-ar-fullscreen):not(.split) {
        top: 0;
        bottom: var(--map-mobile-navigation-drawer-snap-point);
    }

    .pano-actions {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;

        position: absolute;
        z-index: 1021;
        top: 50px;
        left: 8px;
        right: 8px;
        pointer-events: none;

        width: calc(100% - 16px);
        transform: translateY(-100%);

        &.pano-action-fullscreen {
            bottom: 0;
            right: 12px;
            top: auto;
        }

        .pano-actions-right {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 8px;

            pointer-events: auto;
        }

        .pano-actions-left {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            pointer-events: auto;
        }
    }

    .pano-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        position: absolute;
        left: 12px;
        right: 12px;
        bottom: 12px;
        width: calc(100% - 24px);

        .next-step-label {
            font-size: 12pt;
            font-weight: 500;
            color: white;
            text-shadow: 0 2px 8px rgba(0, 0, 0, 0.9);

            //text-transform: capitalize;
        }

        .action-btn {
            background: rgba(255, 255, 255, 0.7);
            backdrop-filter: blur(5px);
            border: 1px solid var(--border-color);
            width: 60px;
            height: 60px;
            // font-variant-numeric: tabular-nums;

            &.btn-translucent-dark {
                border-color: white;
            }

            &.end-btn {
                display: flex;
                align-items: center;
                gap: 4px;
                width: auto;
                height: 60px;
                padding: 0 24px;
            }

            &.simulate-step {
                width: 100%;
                opacity: 0;
            }
        }
    }
}

.navigation-actions {
    display: flex;
    flex-direction: column;
    margin-top: 16px;

    .navigation-actions-button {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        text-align: left;
        gap: 16px;
        padding: 20px 16px;
        background: white;
        color: #5c5c5c;

        i {
            color: var(--color-dark);
        }

        &:active, &:focus {
            background: var(--color-light);
        }
    }

    .navigation-actions-button + .navigation-actions-button {
        border-top: 1px solid var(--border-color);
    }
}

.mobile-stacked-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    margin-left: auto;
    pointer-events: auto;
}

.fake-btn-outside {
    margin-top: 12px;
    margin-bottom: -8px;
}

:deep(.top-actions-menu) {
    .dropdown-toggle {
        width: 30px;
        height: 30px;
        aspect-ratio: 1/1;
        border-radius: 999px;
        padding: 0;
    }
}

.desktop-navigation-list {
    flex: 1;
    min-height: 0;
    background: white;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    overflow-y: auto;
    padding: 0;

    .navigation-item {
        display: flex;
        align-items: center;
        gap: 12px;
        height: fit-content;
        width: 100%;
        padding: 20px 12px 20px 16px;
        background: white;
        height: auto;
        cursor: pointer;

        &:hover {
            background: var(--color-light);
        }
    }

    .navigation-item+.navigation-item {
        border-top: var(--border-width) solid var(--border-color);
    }

    .current-instruction {
        background: var(--color-light-info);
    }
}

.desktop-ar-fullscreen {
    top: 0;
    bottom: 0;
    z-index: 1030;

    .pano-overlay-handle {
        display: none;
    }
}

.desktop-ar-top {
    position: absolute;
    z-index: 1022;
    top: 16px;
    left: 16px;
    right: 16px;
    display: flex;
    align-items: flex-start;
    gap: 12px;

    .desktop-ar-current {
        min-width: 300px;
        margin-inline: auto;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.9);
        backdrop-filter: blur(8px);
        border: 1px solid white;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
    }

    .desktop-ar-top-actions {
        display: flex;
        gap: 8px;
    }
}

.desktop-ar-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 16px;
    z-index: 1022;

    .action-btn {
        min-height: 56px;

        &.end-btn {
            padding-inline: 22px;
        }
    }
}

.preview-mode {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    width: 148px;
    height: 108px;
    padding: 0;

    overflow: hidden;
    pointer-events: auto;

    border-radius: 12px;
    background: #fff;
    border: 1px solid var(--border-color);

    &:has(.preview-mode-map) {
        height: 108px;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .pano-360 {
        width: 100%;
        height: 100%;
        transform: translateZ(0);
        backface-visibility: hidden;
    }

    :deep(.pnlm-container) {
        transform: translateZ(0);
        backface-visibility: hidden;
    }
}

.image-preview {
    position: relative;
    width: 110px;
    min-width: 110px;
    height: 110px;
    aspect-ratio: 1;
    border-radius: 14px;
    border: 2px solid #fff;
    overflow: hidden;
    background: #fff;
    box-shadow: 0px 2px 5px rgba(0, 0, 0, .18);
    padding: 0;
    pointer-events: auto;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
}

.next-step-preview-badge {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: 1px 8px;
    background: rgba(255, 255, 255, 0.95);
}

.next-step-preview-name {
    font-size: 9.5pt;
    font-weight: 700;
    color: var(--color-dark);
}

.desktop-ar-preview {
    position: relative;
    margin-top: 16px;
    width: 100%;
    aspect-ratio: 2;
    border-radius: 8px;
    overflow: hidden;

    .pano-360 {
        height: 100%;
        width: 100%;
    }
}

.desktop-image {
    position: relative;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    overflow: hidden;
    margin-top: 16px;

    .desktop-image-content {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .finish-badge {
        position: absolute;
        top: 12px;
        left: 12px;
        background: var(--color-accent);
        color: white;
        padding: 4px 10px;
        border-radius: 999px;
        font-size: 10pt;
        font-weight: 500;
    }
}

.expand-btn {
    position: absolute;
    bottom: -1px;
    right: -1px;
    width: 28px;
    height: 28px;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;

    border-radius: 16px 0 0 0;
    background: rgba(0, 0, 0, 0.35);
    color: white;
    font-size: 11px;

    &.expand-desktop {
        top: 0;
        right: 0;
        bottom: auto;

        border-radius: 0 0 0 12px;

        cursor: pointer;
        width: auto;
        height: auto;
        padding: 6px 10px;
        background: rgba(0, 0, 0, 0.6);
        font-size: 13px;
        transition: background 200ms ease;

        &:hover {
            background: rgba(0, 0, 0, 1);
        }
    }

    i {
        --fa-rotate-angle: 45deg;
    }
}

.drawer-subtitle {
    font-size: 10pt;
    margin-bottom: 4px;
}

.header-actions-buttons {
    gap: 8px;
}

.navigation-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.current-instruction-info {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 12px;
    border-radius: 12px;
    background: var(--color-lightest);
    border: 1px solid var(--border-color);
}

.placeholder {
    color: var(--color-muted);
}

.display-modal {
    text-align: center;

    .title {
        font-weight: 500;
        font-size: 16pt;
        margin-bottom: 10px;
    }

    .options {
        display: grid;
        gap: 10px;
    }

    .option {
        display: flex;
        gap: 16px;
        align-items: center;
        padding: 10px;
        border-radius: 10px;
        border: 1px solid var(--border-color);
        background: var(--color-lightest);
        user-select: none;
        cursor: pointer;
        color: var(--color-dark);

        &.active {
            border-color: var(--color-accent);
            box-shadow: 0 0 0 2px rgba(0, 95, 229, 0.15);
        }

        .display-label {
            font-size: 14pt;
            font-weight: 500;
            text-align: left;
        }

        .thumb {
            width: 170px;
            height: 110px;
            border-radius: 8px;
            border: 1px solid var(--border-color);
        }
    }
}

.arrival-modal {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.feedback-modal-container {
    ::v-deep .icon {
        width: 80px;
        height: 80px;
        margin: 0 auto 8px;
        border-radius: 50%;
        background: rgba(0, 95, 229, 0.1);
        color: var(--color-accent);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 40px;
        position: absolute;
        top: 10px;
        left: calc(50% - 40px);
    }
}

.feedback-modal {
    margin-top: 30px;
    text-align: center;

    .title {
        font-weight: 700;
        font-size: 14pt;
        text-wrap: pretty;
    }

    .subtitle {
        margin-bottom: 12px;
    }
}

.elevator-modal {
    .floor-visual {
        --elevator-img-size: min(340px, 100%);
        --elevator-icon-size: 118px;

        position: relative;
        margin: 0 auto;
        margin-top: -16px;
        width: var(--elevator-img-size);
        display: flex;
        justify-content: center;
    }

    .floor-indicator {
        position: absolute;
        left: 50%;
        top: 31px;
        transform: translateX(-50%);
        font-size: 15pt;
        background: black;
        border: 2px solid #bdbfc2;
        border-right-width: 3px;
        border-left-width: 3px;
        color: red;
        padding: 0 8px;
        padding-right: 24px;
        font-family: 'Font Awesome 7 Pro';
        font-weight: 600;

        &::after {
            content: '';
            position: absolute;
            right: 5px;
            top: 50%;
            transform: translateY(-50%);
            width: 0;
            height: 0;
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-top: 6px solid red;
        }

        &.direction-up::after {
            border-top: none;
            border-bottom: 6px solid red;
        }
    }

    .elevator-image {
        width: 100%;
    }

    .stair-image {
        width: 200px;
        margin-block: 30px;
    }

    .floor-direction {
        position: absolute;
        left: calc(50% - var(--elevator-icon-size) / 2);
        top: calc(50% - var(--elevator-icon-size) / 2);
        width: var(--elevator-icon-size);
        height: var(--elevator-icon-size);
        border-radius: 50%;
        background: var(--color-accent);
        color: #fff;
        display: grid;
        place-items: center;
        font-size: calc(var(--elevator-icon-size) / 2.4);

        &.direction-up {
            animation: floor-up 2s infinite;
        }

        &.direction-down {
            animation: floor-down 2s infinite;
        }
    }
}

@keyframes floor-up {
    0% {
        opacity: 0;
        transform: translateY(14px);
    }

    20% {
        opacity: 0.85;
        transform: translateY(0);
    }

    60% {
        opacity: 0.85;
        transform: translateY(0);
    }

    100% {
        opacity: 0;
        transform: translateY(-40px);
    }
}

@keyframes floor-down {
    0% {
        opacity: 0;
        transform: translateY(-14px);
    }

    20% {
        opacity: 0.85;
        transform: translateY(0);
    }

    60% {
        opacity: 0.85;
        transform: translateY(0);
    }

    100% {
        opacity: 0;
        transform: translateY(40px);
    }
}

.actions {
    width: 100%;

    .action-btn {
        border-radius: 8px;
    }

    .send-feedback {
        flex: 1;
    }
}

.arrival-modal-header {
    display: flex;
    gap: 8px;
    align-items: center;
    text-align: left;

    .pill {
        background: var(--color-accent);
        color: white;

        i {
            color: white !important;
        }
    }

}

.arrival-image {
    width: 100%;
    height: auto;
    border-radius: 12px;
}

.optional-instruction {
    display: flex;
    align-items: center;

    .optional-instructions {
        font-size: 14pt;
        font-weight: 500;
        text-align: left;
        flex: 1;
    }
}

.optional-instructions-glyph {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--yellow);
    display: grid;
    place-items: center;
    font-size: 18px;
    margin-right: 12px;
}

.feedback-modal {
    .comment-label {
        text-align: left;
        margin-bottom: 6px;
        font-size: 11pt;
        color: var(--color-dark);
    }

    .b-rating {
        font-size: 2rem !important;
    }
}

.abandon-modal {
    text-align: center;

    .icon {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: var(--yellow);
        display: grid;
        place-items: center;
        font-size: 28px;
        margin: 0 auto 16px;
        color: #333;
    }

    .title {
        font-size: 16pt;
        font-weight: 600;
        text-wrap: pretty;
    }

    .subtitle {
        color: var(--color-muted);
        margin-top: 8px;
    }
}

.change-view-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    border-radius: 12px;
}
</style>



